/**
 * The mix: a level trim in dB per sound, on top of its category's gain (sfx/registry.ts), so every
 * sound sits where it should next to the others — hits and clashes up front, footsteps and hovers
 * well under them, ambience under everything. Set from the levels `npm run export:sfx` measures
 * (review/sfx/report.json: peak dBFS, dry): a sound that reads too quiet or loud is trimmed here,
 * not by rewriting its voices.
 */
export const SFX_TRIM_DB: Readonly<Record<string, number>> = {
  'combat.swing.fist.light': 10.5,
  'combat.swing.staff.light': 9.5,
  'combat.swing.staff.heavy': 6.5,
  'combat.swing.claw': 4.5,
  'combat.whiff': 5.5,
  'combat.dodge': 8,
  'combat.hit.blunt': 2.5,
  'combat.hurt': 3,
  'combat.guard': 10.5,
  'combat.block': 3,
  'combat.interrupt': 2.5,
  'combat.charge': 4.5,
  'combat.stun': 7.5,
  'combat.draw': 4,
  'combat.ching': 6,
  'econ.coin': 2,
  'econ.pickup': 8,
  'econ.equip': 5,
  'econ.gather': 7,
  'econ.work.start': 10,
  'move.step.stone': 6.5,
  'move.step.wood': 7,
  'move.step.grass': 8,
  'move.step.snow': 8.5,
  'move.step.water': 8,
  'move.jump': 12,
  'move.airReset': 8.5,
  'move.dash.skill': 8,
  'move.glide': 2,
  'skill.el.soft': 5,
  'skill.ice.shatter': 4,
  'skill.buff': 4,
  'skill.fx.nine': 5,
  'skill.fx.pierce': 9.5,
  'skill.fx.whirl': 5,
  'skill.fx.breath': 3.5,
  'skill.fx.bite': 7.5,
  'skill.fx.wind': 6.5,
  'skill.fx.snow': 3.5,
  'skill.fx.snowdash': 4.5,
  'skill.fx.blizzard': 8,
  'skill.fx.snowstep': 5.5,
  'skill.fx.chain': 5.5,
  'skill.fx.shadow': 3,
  'skill.fx.needles': 10,
  'skill.fx.brush': 7,
  'skill.fx.ink': 5.5,
  'skill.fx.seal': 4,
  'social.chat': 5.5,
  'social.emote': 2,
  'social.emoji': 8.5,
  'social.bark': 12.5,
  'social.dance.hit': 8,
  'social.dance.perfect': 6,
  'social.dance.miss': 4,
  'ui.click': 10,
  'ui.hover': 18.5,
  'ui.paper.open': 3.5,
  'ui.menu.close': 2,
  'ui.tab': 10.5,
  'ui.notice': 3.5,
  'ui.toggle.off': 4,
  'ui.quest.progress': 14.5,
  'ui.trainTick': 9.5,
  'amb.peak': 4,
  'amb.bamboo': 2.5,
  'amb.shrine': 10.5,
  'amb.splash': 6.5,
  'amb.drip': 9,
  'amb.gust': 2,
  'amb.bubble': 3.5,
  'amb.chime': 3,
  'world.lantern': 12,
  'world.carriage': 4.5,
  'world.carriage.arrive': 5,
  'scene.waves': 7,
  'scene.whisper': 12,
  'scene.palmGlow': 6.5,
};

/** A sound's trim as a gain factor. */
export const trimOf = (id: string): number => 10 ** ((SFX_TRIM_DB[id] ?? 0) / 20);
